import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { errorMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/rbac";
import { useDeleteDevice, useDevice } from "../hooks/queries";
import type { RangeKey } from "../hooks/metrics";
import { TabPanel, Tabs, type TabItem } from "../components/ui/Tabs";
import { Button } from "../components/ui/Button";
import { Card, CardContent } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { Skeleton } from "../components/ui/Skeleton";
import { ErrorState } from "../components/ui/States";
import { useToast } from "../components/ui/Toast";
import {
  DeviceStatusBadge,
  OsBadge,
} from "../components/domain/Badges";
import { formatRelative } from "../lib/format";
import { DeviceOverviewTab } from "./device/DeviceOverviewTab";
import { DeviceMetricsTab } from "./device/DeviceMetricsTab";
import { DevicePatchesTab } from "./device/DevicePatchesTab";
import { DeviceHardwareTab } from "./device/DeviceHardwareTab";
import { DeviceEventsTab } from "./device/DeviceEventsTab";

const TAB_ITEMS: readonly TabItem[] = [
  { id: "overview", label: "Genel Bakış" },
  { id: "metrics", label: "Metrikler" },
  { id: "patches", label: "Patch'ler" },
  { id: "hardware", label: "Donanım" },
  { id: "events", label: "Olaylar" },
];

const RANGE_KEYS: readonly RangeKey[] = ["1h", "6h", "24h", "7d"];

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deviceQuery = useDevice(id);
  const { user } = useAuth();
  const { toast } = useToast();
  const deleteMutation = useDeleteDevice();
  const canManageDevices = can(user?.role, "manageDevices");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const tab = searchParams.get("tab") ?? "overview";
  const rangeParam = searchParams.get("range") ?? "24h";
  const range: RangeKey = RANGE_KEYS.includes(rangeParam as RangeKey)
    ? (rangeParam as RangeKey)
    : "24h";

  function setParam(key: string, value: string) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  if (deviceQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (deviceQuery.isError) {
    return (
      <ErrorState
        title="Cihaz bulunamadı"
        message="Cihaz kaydı yüklenemedi. Silinmiş veya erişim yetkiniz olmayan bir cihaz olabilir."
        onRetry={() => void deviceQuery.refetch()}
      />
    );
  }

  const device = deviceQuery.data;

  return (
    <div className="flex flex-col gap-4">
      {/* Başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <nav aria-label="Sayfa yolu" className="text-muted-foreground text-xs">
            <Link to="/devices" className="text-primary hover:underline">
              Cihazlar
            </Link>
            <span aria-hidden="true"> / </span>
            <span>{device.hostname}</span>
          </nav>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight">
            {device.hostname}
            <DeviceStatusBadge status={device.status} />
            <OsBadge osFamily={device.osFamily} />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {device.displayName ? `${device.displayName} · ` : ""}
            Son görülme {formatRelative(device.lastSeen)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/devices")}>
            Listeye dön
          </Button>
          {canManageDevices ? (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              Sil
            </Button>
          ) : null}
        </div>
      </div>

      {/* Sekmeler */}
      <Tabs
        items={TAB_ITEMS}
        value={tab}
        onChange={(next) => setParam("tab", next)}
        ariaLabel="Cihaz detay sekmeleri"
      />

      <TabPanel id="overview" active={tab === "overview"}>
        <DeviceOverviewTab device={device} />
      </TabPanel>
      <TabPanel id="metrics" active={tab === "metrics"}>
        <DeviceMetricsTab
          deviceId={device.id}
          range={range}
          onRangeChange={(next) => setParam("range", next)}
        />
      </TabPanel>
      <TabPanel id="patches" active={tab === "patches"}>
        <DevicePatchesTab deviceId={device.id} />
      </TabPanel>
      <TabPanel id="hardware" active={tab === "hardware"}>
        <DeviceHardwareTab deviceId={device.id} />
      </TabPanel>
      <TabPanel id="events" active={tab === "events"}>
        <DeviceEventsTab deviceId={device.id} />
      </TabPanel>

      {deviceQuery.isFetching && !deviceQuery.isPending ? (
        <Card>
          <CardContent className="text-muted-foreground py-2 text-xs">
            Güncelleniyor…
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Cihaz silinsin mi?"
        description={`${device.hostname} kaydı kalıcı olarak silinecek.`}
      >
        <p className="text-muted-foreground text-sm">
          Cihazla ilişkili envanter, patch, olay ve uyarı kayıtları da kaldırılır. Bu işlem geri
          alınamaz. Agent hâlâ çalışıyorsa cihazı yeniden izlemek için yeni bir enrollment token
          ile yeniden kaydetmeniz gerekir.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirmDelete(false)}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={() => {
              deleteMutation.mutate(device.id, {
                onSuccess: () => {
                  toast(`${device.hostname} silindi.`, "success");
                  navigate("/devices");
                },
                onError: (error) => toast(errorMessage(error), "error"),
              });
            }}
          >
            {deleteMutation.isPending ? "Siliniyor…" : "Sil"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
